import Column from "@/Components/Grid/Column";
import Container from "@/Components/Grid/Container";
import Row from "@/Components/Grid/Row";
import { PageProps } from "@/types";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Head, Link } from "@inertiajs/react";
import { useEffect, useRef, useState } from "react";

import BlockedVideoPlayer from "@/Components/BlockedVideoPlayer";
import { formatWatchTime } from "@/Helpers/formatWatchTime";
import useIsServer from "@/Hooks/useIsServer";
import PatientLayout from "@/Layouts/PatientLayout";
import { ProgressEvent } from "@/types/ProgressEvent";
import { VideoModel } from "@/types/models/VideoModel";
import { Transition } from "@headlessui/react";
import axios from "axios";
import ReactPlayer from "react-player";

const Player = ({ auth, video }: PageProps<{ video: VideoModel }>) => {
    const isServer = useIsServer();

    const [playerReady, setPlayerReady] = useState(false);
    const [hoverIndex, setHoverIndex] = useState<number>(video.rate ? video.rate : -1);
    const [currentRate, setCurrentRate] = useState<number>(video.rate ? video.rate : -1);
    const [showChapters, setShowChapters] = useState<boolean>(true);
    const [lastReportedTime, setLastReportedTime] = useState(0);
    const videoPlayerRef = useRef<ReactPlayer>(null);

    useEffect(() => {
        setCurrentRate(video.rate);
        setHoverIndex(video.rate)
    }, [video])

    useEffect(() => {
        if (playerReady && videoPlayerRef.current && video.history_time) {
            videoPlayerRef.current.seekTo(video.history_time);
        }
    }, [playerReady, video.history_time]);

    useEffect(() => {
        if (lastReportedTime > 0) {
            axios.post(route('app.storeMaterialProgress'), {
                time: lastReportedTime,
                modelId: video.id,
                modelType: 'Video'
            })
        }
    }, [lastReportedTime])

    const convertToSeconds = (time: string) => {
        const parts = time.split(':');
        const seconds = (+parts[0] * 60 * 60) + (+parts[1] * 60) + (+parts[2]);
        return seconds;
    };

    const scrollToTimestop = (timestop: string) => {
        const seconds = convertToSeconds(timestop);

        if (videoPlayerRef.current) {
            videoPlayerRef.current.seekTo(seconds, 'seconds');
        }
    }

    const handleProgress = (progress: ProgressEvent) => {
        if (progress.playedSeconds > 2 && progress.playedSeconds < 3) {
            setLastReportedTime(Math.floor(progress.playedSeconds));
        }

        if (Math.floor(progress.playedSeconds) % 10 === 0 && Math.floor(progress.playedSeconds) !== lastReportedTime) {
            setLastReportedTime(Math.floor(progress.playedSeconds));
        }
    }

    const handlePlayerReady = () => {
        setPlayerReady(true);
    };

    const handleMovieEnd = () => {
        if (!isServer) {
            if (document.fullscreenElement) {
                window.scrollTo(0, 0);
                document.exitFullscreen();
            }
        }
    }

    return (
        <PatientLayout>
            <Head title="Oglądanie filmu" />

            <Container className="my-4">
                <Row>
                    <Column className="text-left">
                        <Link href={'/dla-pacjenta'} className="outline-btn inline-flex items-center gap-x-2 justify-end border-1 border-accordorange">
                            Powrót
                            <Icon icon={`tabler:arrow-back`} width={24} className="text-arcticMist" />
                        </Link>
                    </Column>
                </Row>
            </Container>
            <div className="flex relative items-start flex-wrap">
                <div className="lg:max-w-[75%] max-w-full w-full">
                    <div className="w-full h-full aspect-video player-wrapper relative rounded-lg" id="test">
                        <BlockedVideoPlayer
                            controls
                            url={'/patient' + video.video + '/' + video.id + '/video'}
                            className='absolute inset-0 rounded-lg z-0'
                            width={`100%`}
                            onProgress={handleProgress}
                            onReady={handlePlayerReady}
                            ref={videoPlayerRef}
                            height={`100%`}
                            onEnded={handleMovieEnd}
                        />
                    </div>
                    <div className="movie-meta-info">
                        <Container className="mt-8">
                            <Row>
                                <Column className="mb-4 text-left">
                                    <h1 className="text-arcticMist h3 font-semibold mb-2">{video.name}</h1>
                                    <div className="flex items-center gap-x-4">
                                        <p className="text-arcticMist text-base font-semibold tracking-[0.24px]">Czas oglądania: {formatWatchTime(video.time)}</p>
                                    </div>
                                </Column>
                            </Row>
                            <Row className="mb-12 text-left">
                                <Column xl={9} lg={8} className="maxMd:order-2">
                                    <p className="text-arcticMist text-body tracking-[0.27px]" dangerouslySetInnerHTML={{ __html: video.description.replace(/\n/g, '<br/>') }}>
                                    </p>
                                </Column>
                                <Column xl={3} lg={4} className="maxMd:order-1 maxMd:mb-6">
                                    <p className="text-arcticMist text-body tracking-[0.27px]">Prowadzący:</p>
                                    {video.authors && video.authors.map((author, i) => (
                                        <p key={i} className="text-accordorange text-body tracking-[0.27px]">{`${author.degree} ${author.name_surname}`}</p>
                                    ))}
                                </Column>
                            </Row>
                        </Container>
                    </div>
                </div>
                <div className="lg:max-w-[25%] max-w-full w-full max-h-screen overflow-y-scroll maxMd:mb-8">
                    <button className="bg-shadowNavy px-6 py-4 w-full text-left flex items-center justify-between" type="button" onClick={() => setShowChapters((prev) => !prev)}>
                        <p className="text-arcticMist h4 tracking-[0.48px]">Rozdziały</p>
                        <Icon icon={`ic:round-navigate-next`} width={24} className={`transition-theme ${showChapters ? '-rotate-90 text-accordorange' : 'rotate-90 text-arcticMist'}`} />
                    </button>
                    <Transition
                        show={showChapters}
                        enter="transition ease-out duration-200"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        {video.timestops.map((tm, i) => (
                            <button type="button" onClick={() => scrollToTimestop(tm.start_at)} className="block w-full  max-w-full text-left pl-6 pr-6 py-6 border-b-1 border-accordorange" key={i}>
                                {/* <p className="text-accordorange text-14 leading-18 tracking-[0.21px]">Kategoria</p> */}
                                <p className="text-arcticMist text-base tracking-[0.21px] font-semibold">{tm.name}</p>
                                <div className="flex justify-between items-center mt-1">
                                    <div className="flex gap-x-2 items-center">
                                        <Icon icon={`ph:clock-fill`} width={24} className="text-arcticMist" />
                                        <p className="text-arcticMist text-base tracking-[0.24px] font-semibold">{tm.start_at}</p>
                                    </div>
                                    <div className="flex gap-x-2 items-center">
                                        <Icon icon={`ph:check-circle-fill`} width={24} className="text-arcticMist opacity-50" />
                                        <Icon icon={`material-symbols:bookmark-outline-rounded`} width={24} className="text-arcticMist" />
                                    </div>
                                </div>
                            </button>
                        ))}
                    </Transition>
                </div>
            </div>
        </PatientLayout>
    )
}

export default Player;